import React, { useState, useEffect } from 'react';
import {
  ImagePromptTemplate,
  ImagePromptTemplateCreateRequest,
  ImagePromptTemplateUpdateRequest
} from '../../services/api/imagePromptTemplateService';
import {
  TaskyAlert,
  TaskyButton,
  TaskyDialog,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskyTextarea,
} from '../tasky';

interface ImagePromptTemplateEditModalProps {
  isOpen: boolean;
  template?: ImagePromptTemplate | null;
  onSubmit: (data: ImagePromptTemplateCreateRequest | ImagePromptTemplateUpdateRequest) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const ImagePromptTemplateEditModal: React.FC<ImagePromptTemplateEditModalProps> = ({
  isOpen,
  template,
  onSubmit,
  onClose,
  isLoading = false
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'single' | 'variations'>('single');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setType(template.type);
      setContent(template.content);
    } else {
      setName('');
      setType('single');
      setContent('');
    }
    setError(null);
  }, [template, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      setError('テンプレート名を入力してください。');
      return;
    }
    if (!content.trim()) {
      setError('プロンプト内容を入力してください。');
      return;
    }

    const data: ImagePromptTemplateCreateRequest | ImagePromptTemplateUpdateRequest = {
      name: name.trim(),
      type,
      content: content.trim()
    };

    onSubmit(data);
  };

  if (!isOpen) return null;

  return (
    <TaskyDialog
      open={isOpen}
      onClose={onClose}
      title={template ? 'プロンプトテンプレート編集' : 'プロンプトテンプレート新規作成'}
      size="xl"
      className="max-w-[800px]"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

        <TaskyFormGroup label="テンプレート名" htmlFor="name">
          <TaskyInput
            type="text"
            id="name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="例: GPT-5.2 標準プロンプト"
            disabled={isLoading}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="タイプ"
          htmlFor="type"
          helper="単一生成用: 1つのプロンプトを生成 / バリエーション生成用: 複数のプロンプト案を生成"
        >
          <TaskySelect
            id="type"
            value={type}
            onChange={e => setType(e.target.value as 'single' | 'variations')}
            disabled={isLoading}
          >
            <option value="single">単一生成用 (Single)</option>
            <option value="variations">バリエーション生成用 (Variations)</option>
          </TaskySelect>
        </TaskyFormGroup>

        <TaskyFormGroup label="プロンプト内容" htmlFor="content">
          <TaskyTextarea
            id="content"
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="システムプロンプトを入力してください..."
            disabled={isLoading}
            className="min-h-[300px] font-mono text-sm leading-relaxed"
          />
          <div className="mt-2 rounded-tasky-sm bg-warning/10 px-3 py-2.5 text-xs text-dark">
            <strong>利用可能な変数:</strong>{' '}
            <code className="bg-warning/20 px-1 py-0.5 rounded font-mono">{'{targetCharCount}'}</code> (目標文字数),{' '}
            <code className="bg-warning/20 px-1 py-0.5 rounded font-mono">{'{maxCharCount}'}</code> (最大文字数)
            {type === 'variations' && (
              <>
                , <code className="bg-warning/20 px-1 py-0.5 rounded font-mono">{'{count}'}</code> (バリエーション数)
              </>
            )}
          </div>
        </TaskyFormGroup>

        <div className="flex justify-end gap-3 border-t border-warm-border pt-4">
          <TaskyButton
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            キャンセル
          </TaskyButton>
          <TaskyButton type="submit" size="sm" disabled={isLoading} isLoading={isLoading}>
            {isLoading ? '保存中...' : '保存'}
          </TaskyButton>
        </div>
      </form>
    </TaskyDialog>
  );
};

export default ImagePromptTemplateEditModal;
